/**
 * HouseholdMilestoneCelebration Component
 * =======================================
 * A Duolingo-style bouncy modal that slides up when a household friend
 * hits a 7/14/30-day savings streak milestone.
 *
 * Features:
 * - Spring physics slide-up animation
 * - Victory Lottie/CSS confetti animation
 * - Friend's avatar + streak number
 * - "Send Congrats" button → fires push notification + in-app notif
 * - Auto-acknowledges milestone in DB so it stops showing
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { apiPost } from '@/utils/api';
import { GuapoMascot } from '@/components/GuapoMascot';

const MILESTONE_BADGES = {
  7: { emoji: '🔥', title: 'On Fire', color: 'from-orange-400 to-red-500' },
  14: { emoji: '⚡', title: 'Lightning Streak', color: 'from-yellow-400 to-orange-500' },
  30: { emoji: '👑', title: 'Mogul Status', color: 'from-purple-500 to-pink-500' },
};

function ConfettiBurst() {
  // CSS-based confetti burst (no external assets needed)
  const pieces = Array.from({ length: 24 });
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-3xl">
      {pieces.map((_, i) => {
        const left = (i * 4.16) % 100;
        const delay = (i % 8) * 0.05;
        const dur = 1.6 + (i % 4) * 0.2;
        const colors = ['#fbbf24', '#f472b6', '#34d399', '#60a5fa', '#a78bfa'];
        const color = colors[i % colors.length];
        return (
          <motion.div
            key={i}
            initial={{ y: -20, x: `${left}%`, rotate: 0, opacity: 1 }}
            animate={{
              y: 320,
              rotate: 720,
              opacity: [1, 1, 0],
            }}
            transition={{ duration: dur, delay, ease: 'easeIn' }}
            className="absolute top-0 w-2 h-3 rounded-sm"
            style={{ backgroundColor: color }}
          />
        );
      })}
    </div>
  );
}

export function HouseholdMilestoneCelebration({ milestone, onDismiss }) {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  if (!milestone) return null;

  const data = milestone.data || {};
  const streakDays = data.streak_days || 7;
  const achieverName = data.achiever_name || 'Your friend';
  const achieverAvatar = data.achiever_avatar;
  const eventId = data.event_id;
  const badge = MILESTONE_BADGES[streakDays] || MILESTONE_BADGES[7];

  const handleSendCongrats = async () => {
    if (sending || sent) return;
    setSending(true);
    try {
      await apiPost(`/user/milestones/${eventId}/send-congrats`, {});
      setSent(true);
      // Auto-close after a beat
      setTimeout(() => onDismiss(), 1200);
    } catch (e) {
      console.error('send-congrats failed', e);
      setSending(false);
    }
  };

  const handleSkip = async () => {
    try {
      await apiPost(`/user/milestones/${eventId}/acknowledge`, {});
    } catch (e) {
      console.error('acknowledge failed', e);
    }
    onDismiss();
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[110] flex items-end sm:items-center justify-center p-4"
        onClick={handleSkip}
        data-testid="milestone-celebration-overlay"
      >
        <motion.div
          initial={{ y: 600, scale: 0.9, opacity: 0 }}
          animate={{ y: 0, scale: 1, opacity: 1 }}
          exit={{ y: 600, scale: 0.9, opacity: 0 }}
          transition={{
            type: 'spring',
            stiffness: 220,
            damping: 22,
            mass: 1,
          }}
          className="relative w-full max-w-sm bg-gradient-to-b from-white to-yellow-50 rounded-3xl shadow-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          data-testid="milestone-celebration-modal"
        >
          <ConfettiBurst />

          {/* Top gradient banner with Guapo hero */}
          <div className={`bg-gradient-to-r ${badge.color} pt-8 pb-4 px-6 text-center text-white relative overflow-visible`}>
            {/* Guapo bounces dramatically from the bottom into the spotlight */}
            <div className="flex justify-center mb-2 relative z-[2]">
              <GuapoMascot
                mode="celebrating"
                size="lg"
                data-testid="guapo-celebrating"
                className="drop-shadow-[0_8px_30px_rgba(0,0,0,0.35)]"
              />
            </div>
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: 'spring', stiffness: 260, damping: 14, delay: 0.55 }}
              className="text-3xl"
            >
              {badge.emoji}
            </motion.div>
            <p className="text-xs font-bold uppercase tracking-widest opacity-80 mt-1">
              Household Milestone
            </p>
            <h2 className="text-2xl font-extrabold mt-1" data-testid="milestone-title">
              {badge.title}!
            </h2>
          </div>

          {/* Body */}
          <div className="p-6 text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 300, damping: 15, delay: 0.3 }}
              className="flex justify-center mb-3"
            >
              {achieverAvatar ? (
                <img
                  src={achieverAvatar}
                  alt={achieverName}
                  className="w-20 h-20 rounded-full ring-4 ring-yellow-400 object-cover"
                />
              ) : (
                <div className="w-20 h-20 rounded-full ring-4 ring-yellow-400 bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white text-3xl font-bold">
                  {achieverName.charAt(0).toUpperCase()}
                </div>
              )}
            </motion.div>

            <p className="text-lg font-bold text-gray-900" data-testid="achiever-name">
              {achieverName}
            </p>
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 280, damping: 14, delay: 0.45 }}
              className="my-3"
            >
              <span
                className="text-5xl font-black bg-gradient-to-r from-orange-500 to-pink-500 bg-clip-text text-transparent"
                data-testid="streak-days"
              >
                {streakDays}
              </span>
              <span className="text-lg font-bold text-gray-700 ml-1">day streak</span>
            </motion.div>

            <p className="text-sm text-gray-600 mb-5">
              Just hit a savings goal {streakDays} days in a row. Keep the momentum rolling!
            </p>

            {/* CTA Buttons */}
            <motion.button
              whileTap={{ scale: 0.95 }}
              whileHover={{ scale: 1.02 }}
              onClick={handleSendCongrats}
              disabled={sending || sent}
              className={`w-full py-4 rounded-2xl font-bold text-lg shadow-lg transition-all ${
                sent
                  ? 'bg-green-500 text-white'
                  : `bg-gradient-to-r ${badge.color} text-white hover:shadow-xl`
              } ${sending ? 'opacity-70' : ''}`}
              data-testid="send-congrats-btn"
            >
              {sent ? '✅ Congrats Sent!' : sending ? 'Sending...' : '🎉 Send Congrats'}
            </motion.button>

            <button
              onClick={handleSkip}
              className="mt-3 text-sm text-gray-500 font-medium hover:text-gray-700"
              data-testid="skip-milestone-btn"
            >
              Maybe later
            </button>
          </div>

          {/* Bottom rainbow accent */}
          <div className="h-2 bg-gradient-to-r from-orange-400 via-pink-500 to-purple-500" />
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default HouseholdMilestoneCelebration;
